import { inject, Injectable, signal } from '@angular/core';
import { MessageService } from 'primeng/api';
import { environment } from 'src/environments/environment';
import { SocketService } from '../api/socket.service';
import { SignaturesService } from '../api/signatures.service';
import { LogEntry, Signer, SigSession, XodoResult } from '@/models/sig-request.model';
import { BinderService } from '../api/binder.service';

interface parsedEvent {
  summary: string;
  details: string;
  severity: "success" | "secondary" | "info" | "danger" | "contrast" | "warn" | undefined;
  statusTag: string;
  date: string;
  timestamp: number;
  signer: string;
}

@Injectable({
  providedIn: 'root'
})
export class SignHistoryService {
  esDoc = signal<XodoResult | null>(null);
  lastSigner = signal<Signer | null>(null);
  events = signal<parsedEvent[]>([]);
  binderService = inject(BinderService);

  private BASE_URL = environment.landmark_API_URL + '/signatures';
  private selectedSession = signal<SigSession>(new SigSession());
  public selectedSignURL = signal('');
  

  constructor(
    private messageService: MessageService, 
    private socketService: SocketService,
    private signaturesService: SignaturesService
  ) {

   }

  getHistory(hash: string) {
    this.signaturesService.getHistory(hash).subscribe((history) => {
      console.log('history', history);
      this.esDoc.set(history);
      this.parseEvents(history);
    });
  }

  sendReminder() {
    const signerId = this.lastSigner()?.id;
    const name = this.lastSigner()?.name;
    if(this.esDoc() && signerId) {
      this.signaturesService.sendReminder(this.esDoc()!.document_hash, signerId).subscribe((response) => {
        console.log('response', response);
        if(response.success) {
          this.messageService.add({severity:'success', summary: 'Reminder Sent', detail: 'Reminder sent to ' + name});
        } else {
          this.messageService.add({severity:'error', summary: 'Reminder Failed', detail: 'Reminder failed to send to ' + name});
        }
      });
    }
  }

  cancelSignatureRequest(binderId: number) {
    if(this.esDoc()) {
      this.signaturesService.cancelSignatureRequest(this.esDoc()!.document_hash, binderId).subscribe((response) => {
        console.log('response', response);
        if(response.success || response.status === 200) {
          this.messageService.add({severity:'success', summary: 'Signature Request Cancelled', detail: 'Signature request cancelled'});
          this.getHistory(this.esDoc()!.document_hash);
          this.binderService.patchBinder({ProjectStatus: 3, ES_Hash: null});
        } else {
          this.messageService.add({severity:'error', summary: 'Signature Cancelation failed', detail: 'Failed to cancel signature request'});
        }
      });
    } else {
      this.messageService.add({severity:'error', summary: 'Signature Request Failed', detail: 'Failed to cancel signature request, no document hash found'});
    }
  }

  cancelDraftingRequest(binderId: number, message: string, user: string) {
    if(this.esDoc()) {
      this.signaturesService.cancelDraftingRequest(this.esDoc()!.document_hash, binderId, message, user).subscribe((response) => {
        console.log('response', response);
        if(response.success || response.status === 200) {
          this.messageService.add({severity:'success', summary: 'Signature Request Cancelled', detail: 'Signature request cancelled'});
          this.getHistory(this.esDoc()!.document_hash);
        } else {
          this.messageService.add({severity:'error', summary: 'Signature Cancelation failed', detail: 'Failed to cancel signature request'});
        }
      });
    } else {
      this.messageService.add({severity:'error', summary: 'Signature Request Failed', detail: 'Failed to cancel signature request, no document hash found'});
    }
  }


  parseEvents(esDoc: XodoResult) {
    const signers = esDoc.signers;
    const events = esDoc.log;
    console.log('events', events);
    let lastSignerPending: LogEntry | null = null;

    const nonSignerEvents = events.filter(e => e.signer === '');
    const latestSignerEventsMap = new Map<string | number, LogEntry>();

    events.forEach(e => {
        if (typeof e.signer === 'number' || (typeof e.signer === 'string' && e.signer !== '')) {
            const currentLatestEvent = latestSignerEventsMap.get(e.signer);
            if (!currentLatestEvent || new Date(e.timestamp).getTime() > new Date(currentLatestEvent.timestamp).getTime()) {
                latestSignerEventsMap.set(e.signer, e);
            }
        }
    });

    // Convert the map values to an array to get the latest events
    const signerEvents = Array.from(latestSignerEventsMap.values());

    // Combine signerEvents and nonSignerEvents
    const allEvents = [...signerEvents, ...nonSignerEvents];

    // Map the latest signer events to parsedEvent objects
    const parsedEvents = allEvents.map(event => {
      let summary = '';
      let severity: "success" | "secondary" | "info" | "danger" | "contrast" | "warn" | undefined;
      let statusTag = '';
      const signerName = signers.find(s => s.id === event.signer)?.name || '';
      switch (event.event) {
          case 'document_created':
              summary = 'Signature Request Received';
              severity = 'success';
              statusTag = 'Created';
              break;
          case 'document_edited':
              summary = 'Signature Request Edited';
              severity = 'info';
              statusTag = 'Pending';
              break;
          case 'document_completed':
              summary = 'All Signatures Completed. Binder Sent to Drafting.';
              severity = 'success';
              statusTag = 'Completed';
              break;
          case 'document_sent':
              summary = 'Request has been sent to ' + signerName;
              severity = 'warn';
              statusTag = 'Pending';
              if(lastSignerPending === null || lastSignerPending.timestamp < event.timestamp) {
                lastSignerPending = event;
              }
              break;
          case 'document_restored':
              summary = 'Document has been restored from trash';
              severity = 'info';
              statusTag = 'Pending';
              break;
          case 'document_cancelled':
              summary = 'Signature request cancelled';
              severity = 'danger';
              statusTag = 'Cancelled';
              break;
          case 'document_trashed':
              summary = 'Document has been trashed';
              severity = 'danger';
              statusTag = 'Cancelled';
              break;
          case 'document_archived':
              summary = 'Document has been archived';
              severity = 'info';
              statusTag = 'Pending';
              break;
          case 'document_unarchived':
              summary = 'Document has been unarchived';
              severity = 'warn';
              statusTag = 'Pending';
              break;
          case 'document_deleted':
              summary = 'Document has been deleted';
              severity = 'danger';
              statusTag = 'Cancelled';
              break;
          case 'document_expired':
              summary = 'Signature request expired';
              severity = 'warn';
              statusTag = 'Pending';
              break;
          case 'document_viewed':
              summary = 'Request has been opened/viewed by ' + signerName;
              severity = 'warn';
              statusTag = 'Pending';
              if(lastSignerPending === null || lastSignerPending.timestamp < event.timestamp) {
                lastSignerPending = event;
              }
              break;
          case 'document_declined':
              summary = 'Request has been declined by ' + signerName;
              severity = 'danger';
              statusTag = 'Declined';
              break;
          case 'document_signed':
              summary = 'Document has been signed by ' + signerName;
              severity = 'success';
              statusTag = 'Signed';
              break;
          default:
              summary = 'Unknown Event Type';
              severity = 'info';
              statusTag = 'Pending';
      }

        return {
            summary,
            details: '',
            date: new Date(event.timestamp * 1000).toLocaleString(),
            timestamp: event.timestamp,
            signer: signerName,
            severity,
            statusTag
        };
    });
    console.log('lastSignerPending', lastSignerPending);
    if(lastSignerPending !== null) {
      this.lastSigner.set(signers.find(s => s.id === lastSignerPending!.signer) || null);
      console.log('lastSigner', this.lastSigner);
    }
    this.events.set(this.sortEvents(parsedEvents));
}

  sortEvents(events: parsedEvent[]) {
    if(events) {
      return events.sort((a, b) => {
        return b.timestamp - a.timestamp;
      });
    }
    return [];
  }
  
  
}
